import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLoading } from '../../hooks/useLoading';
import { useNotifications } from '../../hooks/useNotifications';
import {
  getDocumentRepositoryFileById,
  downloadVersionRequest
} from '../../constants/requests';

export default function FileDetailsDialog({
  open,
  fileId,
  employeeId,
  onClose
}) {
  const { request } = useLoading();
  const { showNotifications } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [openingLatest, setOpeningLatest] = useState(false);

  useEffect(() => {
    if (!open || !fileId) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await request(() =>
          getDocumentRepositoryFileById(fileId, employeeId)
        );
        if (res?.data?.success && res?.data?.data) {
          setFile(res.data.data);
        } else {
          setFile(null);
        }
      } catch (err) {
        setFile(null);
        showNotifications('error', 'Failed to load file details', 4000);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
    // eslint-disable-next-line
  }, [open, fileId]);

  const doc =
    file?.documentManagerId && typeof file.documentManagerId === 'object'
      ? file.documentManagerId
      : null;
  const documentManagerId = doc?._id ?? null;
  const displayName = doc?.name ?? doc?.documentName ?? doc?.title ?? 'File';

  const handleOpenLatestVersion = async () => {
    if (!documentManagerId) return;
    setOpeningLatest(true);
    try {
      const res = await request(() =>
        downloadVersionRequest(documentManagerId)
      );
      const signedUrl =
        res?.data?.body?.signedUrl ?? res?.data?.data?.signedUrl;
      if (signedUrl) {
        window.open(signedUrl, '_blank', 'noopener,noreferrer');
      } else {
        showNotifications(
          'error',
          'Could not get download URL for latest version',
          4000
        );
      }
    } catch (err) {
      showNotifications(
        'error',
        err?.response?.data?.message ?? 'Failed to open latest version',
        4000
      );
    } finally {
      setOpeningLatest(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>File details</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Box>
        ) : file ? (
          <>
            <Typography variant="subtitle1">{displayName}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              File ID: {file?._id ?? file?.id}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Creator: {file?.creatorId ?? '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Document Manager ID: {doc?._id ?? file?.documentManagerId ?? '—'}
            </Typography>
            {documentManagerId ? (
              <Button
                sx={{ mt: 2 }}
                variant="outlined"
                startIcon={
                  openingLatest ? (
                    <CircularProgress size={18} />
                  ) : (
                    <OpenInNewIcon />
                  )
                }
                disabled={openingLatest}
                onClick={handleOpenLatestVersion}
              >
                {openingLatest ? 'Opening…' : 'Open latest version'}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No document URL available to open.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No file details available.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
